import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useEffect, useState, type ReactElement } from "react";
import { ConfirmDialog, ContentHeader, Field, Modal, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type {
  FiscalFileLink,
  InboundDfeItemView,
  InboundDfeView,
  InboundPurchaseOrderCandidate,
  ListResult,
  ProductListItem,
  WarehouseView,
} from "@cx/contracts";
import {
  MANIFEST_KINDS,
  eventTextProblem,
  formatBRL,
  formatDateBR,
  formatDocument,
  formatQty,
  todayInSaoPaulo,
  type ManifestKind,
} from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { dateTimeBR, useOpenKey } from "../invoices/InvoiceEvents";
import { accessKeyGroups } from "../invoices/labels";
import {
  EVENT_STATUS_LABEL,
  EVENT_STATUS_TONE,
  IMPORT_LABEL,
  IMPORT_TONE,
  MANIFEST_LABEL,
  MANIFEST_TONE,
  MATCH_SOURCE_LABEL,
  SEFAZ_LABEL,
  SEFAZ_TONE,
  inboundTitle,
  manifestInFlight,
} from "./labels";

const MANIFEST_HELP: Record<ManifestKind, string> = {
  ciencia:
    "Informa à SEFAZ que a empresa sabe da nota. Libera o download do XML completo; não é conclusiva (depois confirme ou recuse).",
  confirmacao: "Confirma que a operação aconteceu e a mercadoria foi recebida. É conclusiva.",
  desconhecimento: "Declara que a empresa não reconhece esta nota (emissão indevida). É conclusiva.",
  nao_realizada:
    "A operação foi combinada mas não aconteceu (ex.: mercadoria devolvida na entrega). É conclusiva.",
};

/**
 * Nota de entrada (T2.5, RF-SUP-04): dados da nota, manifestação do destinatário com a linha do tempo, vínculo dos itens
 * com os produtos (automático, manual ou sugerido pelo Conciliador de XML — a sugestão só vale depois do aceite) e o
 * lançamento da entrada (estoque com custo + contas a pagar), estornável.
 */
export function InboundPage(): ReactElement {
  const { id } = useParams({ from: "/suprimentos/notas-de-entrada/$id" });
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const canUpdate = can(perms, "inbound_invoices", "update");
  const toast = useToast();
  const key = ["inbound-dfe", companyId, "view", id];
  const qc = useQueryClient();
  const doc = useQuery({
    queryKey: key,
    refetchOnMount: "always",
    queryFn: () => api<InboundDfeView>(`/inbound-dfe/${id}`),
    refetchInterval: (query) => (query.state.data?.events.some(manifestInFlight) ? 2500 : false),
  });
  const set = (d: InboundDfeView) => {
    qc.setQueryData(key, d);
    void qc.invalidateQueries({ queryKey: ["inbound-dfe", companyId, "list"] });
  };
  const xml = useMutation({
    mutationFn: () => api<FiscalFileLink>(`/inbound-dfe/${id}/xml`),
    onSuccess: (l) => window.open(l.url, "_blank", "noopener"),
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const d = doc.data;
  return (
    <>
      <ContentHeader
        title={d ? inboundTitle(d) : "Nota de entrada"}
        breadcrumb={["Suprimentos", "Notas de entrada", d ? inboundTitle(d) : "…"]}
        actions={
          d && (
            <div className="d-flex gap-2">
              <Link to="/suprimentos/notas-de-entrada" className="btn btn-outline-secondary">
                Voltar
              </Link>
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={xml.isPending}
                onClick={() => xml.mutate()}
              >
                Baixar XML
              </button>
            </div>
          )
        }
      />
      <QueryStatus loading={doc.isLoading} error={doc.error} />
      {d && (
        <>
          <HeaderCard d={d} />
          <ManifestCard d={d} canUpdate={canUpdate} onChange={() => void doc.refetch()} />
          {d.xmlStatus === "summary" ? (
            <div className="alert alert-info" role="status">
              Só o resumo desta nota chegou da SEFAZ. Dê ciência da operação para receber o XML completo com
              os itens; a busca seguinte traz o XML. Se tiver o arquivo do fornecedor, use Enviar XML na
              lista.
            </div>
          ) : (
            <>
              <ItemsCard d={d} canUpdate={canUpdate} onChange={set} />
              <EntryCard d={d} perms={perms} onChange={set} />
            </>
          )}
        </>
      )}
    </>
  );
}

function HeaderCard({ d }: { d: InboundDfeView }): ReactElement {
  return (
    <div className="box card mb-3">
      <div className="card-body">
        <div className="d-flex flex-wrap gap-2 mb-3">
          {d.environment === 2 && (
            <span className="badge text-bg-warning">Homologação · sem valor fiscal</span>
          )}
          <StatusBadge tone={SEFAZ_TONE[d.sefazStatus]}>{SEFAZ_LABEL[d.sefazStatus]}</StatusBadge>
          {d.manifestStatus && (
            <StatusBadge tone={MANIFEST_TONE[d.manifestStatus]}>
              {MANIFEST_LABEL[d.manifestStatus]}
            </StatusBadge>
          )}
          <StatusBadge tone={IMPORT_TONE[d.importStatus]}>{IMPORT_LABEL[d.importStatus]}</StatusBadge>
          <span className="badge text-bg-light">
            {d.source === "upload" ? "XML enviado" : "Distribuição da SEFAZ"}
          </span>
        </div>
        <dl className="row mb-0">
          <dt className="col-sm-3">Fornecedor</dt>
          <dd className="col-sm-9">
            {d.issuerName ?? "—"}
            {d.issuerDocument && <span className="text-muted ms-2">{formatDocument(d.issuerDocument)}</span>}
            {d.supplier && (
              <Link className="ms-2 small" to="/cadastros/contatos/$id" params={{ id: d.supplier.id }}>
                ver cadastro
              </Link>
            )}
          </dd>
          <dt className="col-sm-3">Emissão</dt>
          <dd className="col-sm-9">
            {d.issuedAt ? formatDateBR(todayInSaoPaulo(new Date(d.issuedAt))) : "—"}
          </dd>
          <dt className="col-sm-3">Valor</dt>
          <dd className="col-sm-9" data-testid="inbound-total">
            {formatBRL(d.totalCents)}
          </dd>
          <dt className="col-sm-3">Chave de acesso</dt>
          <dd className="col-sm-9 font-monospace small">{accessKeyGroups(d.accessKey)}</dd>
          {d.protocol && (
            <>
              <dt className="col-sm-3">Protocolo</dt>
              <dd className="col-sm-9">{d.protocol}</dd>
            </>
          )}
          {d.installments.length > 0 && (
            <>
              <dt className="col-sm-3">Duplicatas</dt>
              <dd className="col-sm-9">
                {d.installments.map((p, i) => (
                  <div key={i}>
                    {p.number ?? i + 1} · {formatDateBR(p.dueDate)} · {formatBRL(p.amountCents)}
                  </div>
                ))}
              </dd>
            </>
          )}
        </dl>
      </div>
    </div>
  );
}

// ---------- manifestação ----------

function ManifestCard({
  d,
  canUpdate,
  onChange,
}: {
  d: InboundDfeView;
  canUpdate: boolean;
  onChange(): void;
}): ReactElement {
  const { api } = useSession();
  const toast = useToast();
  const [kind, setKind] = useState<ManifestKind | null>(null);
  const refresh = useMutation({
    mutationFn: (eventId: string) =>
      api(`/inbound-dfe/manifest-events/${eventId}/refresh`, { method: "POST" }),
    onSuccess: () => {
      toast("info", "Consulta enviada à SEFAZ");
      onChange();
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const allowed = MANIFEST_KINDS.filter((k) => d.allowedManifests.includes(k));
  return (
    <div className="box card mb-3">
      <div className="card-body">
        <h2 className="h6">Manifestação do destinatário</h2>
        {canUpdate && allowed.length > 0 && (
          <div className="d-flex flex-wrap gap-2 mb-3">
            {allowed.map((k) => (
              <button
                key={k}
                type="button"
                className={`btn btn-sm ${k === "confirmacao" ? "btn-primary" : k === "ciencia" ? "btn-outline-primary" : "btn-outline-danger"}`}
                onClick={() => setKind(k)}
              >
                {MANIFEST_LABEL[k]}
              </button>
            ))}
          </div>
        )}
        {d.events.length === 0 ? (
          <p className="text-muted small mb-0">Nenhuma manifestação enviada.</p>
        ) : (
          <ul className="list-group" aria-label="Manifestações">
            {d.events.map((e) => (
              <li key={e.id} className="list-group-item d-flex flex-wrap align-items-center gap-2">
                <span className="fw-semibold">{MANIFEST_LABEL[e.kind]}</span>
                <StatusBadge tone={EVENT_STATUS_TONE[e.status]}>{EVENT_STATUS_LABEL[e.status]}</StatusBadge>
                <span className="text-muted small">{dateTimeBR(e.registeredAt ?? e.createdAt)}</span>
                {e.protocol && <span className="text-muted small">Protocolo {e.protocol}</span>}
                {e.statusMessage && (
                  <span className="small w-100">
                    {e.statusCode ? `${e.statusCode} – ` : ""}
                    {e.statusMessage}
                  </span>
                )}
                {e.text && <span className="small text-muted w-100">Justificativa: {e.text}</span>}
                {canUpdate && e.canRefresh && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary ms-auto"
                    disabled={refresh.isPending}
                    onClick={() => refresh.mutate(e.id)}
                  >
                    Consultar de novo
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      <ManifestDialog
        d={d}
        kind={kind}
        onClose={() => setKind(null)}
        onDone={() => {
          setKind(null);
          onChange();
        }}
      />
    </div>
  );
}

function ManifestDialog({
  d,
  kind,
  onClose,
  onDone,
}: {
  d: InboundDfeView;
  kind: ManifestKind | null;
  onClose(): void;
  onDone(): void;
}): ReactElement {
  const { api } = useSession();
  const toast = useToast();
  const open = kind !== null;
  const [text, setText] = useState("");
  const [touched, setTouched] = useState(false);
  const { key, renew } = useOpenKey(open);
  useEffect(() => {
    if (open) {
      setText("");
      setTouched(false);
    }
  }, [open]);
  const needsText = kind === "nao_realizada";
  const problem = needsText ? eventTextProblem(text, "reason") : null;
  const send = useMutation({
    mutationFn: () =>
      api(`/inbound-dfe/${d.id}/manifest`, {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({ kind, justification: needsText ? text : null }),
      }),
    onSuccess: () => {
      toast("info", "Manifestação enviada à SEFAZ");
      onDone();
    },
    onError: (e) => {
      toast("danger", errorMessage(e));
      renew(e);
    },
  });
  return (
    <Modal
      open={open}
      title={kind ? MANIFEST_LABEL[kind] : ""}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={send.isPending}
            onClick={() => (problem ? setTouched(true) : send.mutate())}
          >
            {send.isPending ? "Enviando…" : "Enviar manifestação"}
          </button>
        </>
      }
    >
      {kind && <p className="small">{MANIFEST_HELP[kind]}</p>}
      <p className="small text-muted">
        {inboundTitle(d)} · {d.issuerName ?? "—"} · {formatBRL(d.totalCents)}
      </p>
      {needsText && (
        <div className="row">
          <Field
            label="Justificativa"
            required
            className="col-12"
            error={touched ? (problem ?? undefined) : undefined}
            hint={`${text.trim().length}/255 caracteres (mínimo 15)`}
          >
            <textarea
              className="form-control"
              rows={3}
              maxLength={255}
              value={text}
              data-autofocus
              onChange={(e) => setText(e.target.value)}
              onBlur={() => setTouched(true)}
            />
          </Field>
        </div>
      )}
    </Modal>
  );
}

// ---------- itens e vínculo ----------

function ItemsCard({
  d,
  canUpdate,
  onChange,
}: {
  d: InboundDfeView;
  canUpdate: boolean;
  onChange(d: InboundDfeView): void;
}): ReactElement {
  const { api } = useSession();
  const toast = useToast();
  const [editing, setEditing] = useState<InboundDfeItemView | null>(null);
  const editable = canUpdate && d.importStatus !== "imported";
  const ai = useMutation({
    mutationFn: () =>
      api<{ created: number; items: number; dfe: InboundDfeView }>(`/inbound-dfe/${d.id}/ai-match`, {
        method: "POST",
      }),
    onSuccess: (r) => {
      onChange(r.dfe);
      toast(
        "info",
        r.created
          ? `${r.created} ${r.created === 1 ? "sugestão" : "sugestões"} da IA para revisar`
          : "A IA não encontrou sugestões",
      );
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const rematch = useMutation({
    mutationFn: () => api<InboundDfeView>(`/inbound-dfe/${d.id}/rematch`, { method: "POST" }),
    onSuccess: (v) => {
      onChange(v);
      toast("success", "Vínculos refeitos");
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const decide = useMutation({
    mutationFn: (v: { id: string; decision: "accept" | "reject" }) =>
      api(`/ai/suggestions/${v.id}/${v.decision}`, { method: "POST" }),
    onSuccess: async (_r, v) => {
      onChange(await api<InboundDfeView>(`/inbound-dfe/${d.id}`));
      toast("success", v.decision === "accept" ? "Sugestão aceita" : "Sugestão recusada");
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  return (
    <div className="box card mb-3">
      <div className="card-body pb-0 d-flex flex-wrap align-items-center gap-2">
        <h2 className="h6 mb-0 flex-grow-1">
          Itens{" "}
          {d.unmatchedCount > 0 && (
            <span className="badge text-bg-warning ms-1" data-testid="inbound-unmatched">
              {d.unmatchedCount} sem vínculo
            </span>
          )}
        </h2>
        {editable && (
          <>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={rematch.isPending}
              onClick={() => rematch.mutate()}
            >
              Vincular automaticamente
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              disabled={ai.isPending || d.unmatchedCount === 0}
              onClick={() => ai.mutate()}
            >
              {ai.isPending ? "Consultando a IA…" : "Conciliar com IA"}
            </button>
          </>
        )}
      </div>
      <div className="table-responsive">
        <table className="table align-middle mb-0" aria-label="Itens da nota">
          <thead>
            <tr>
              <th scope="col">Item da nota</th>
              <th scope="col" className="text-end">
                Qtd.
              </th>
              <th scope="col" className="text-end">
                Custo
              </th>
              <th scope="col">Produto</th>
              <th scope="col" className="text-end">
                Entra no estoque
              </th>
              {editable && <th scope="col" aria-label="Ações" />}
            </tr>
          </thead>
          <tbody>
            {d.items.map((it) => (
              <tr key={it.id} data-testid={`inbound-item-${it.position}`}>
                <td>
                  <div>{it.description}</div>
                  <div className="text-muted small">
                    {it.code}
                    {it.ncm && <> · NCM {it.ncm}</>}
                    {it.gtin && <> · GTIN {it.gtin}</>}
                  </div>
                </td>
                <td className="text-end text-nowrap">
                  {formatQty(it.quantityQ4)} {it.unit}
                </td>
                <td className="text-end">{formatBRL(it.costCents)}</td>
                <td>
                  {it.productId ? (
                    <>
                      <Link to="/cadastros/produtos/$id" params={{ id: it.productId }}>
                        {it.productName}
                      </Link>
                      {it.matchSource && (
                        <span className="text-muted small ms-2">{MATCH_SOURCE_LABEL[it.matchSource]}</span>
                      )}
                    </>
                  ) : it.createProduct ? (
                    <span>
                      Cadastrar novo: <strong>{it.newProductName ?? it.description}</strong>
                    </span>
                  ) : (
                    <span className="text-warning-emphasis">Sem vínculo</span>
                  )}
                  {it.needsReview && <span className="badge text-bg-warning ms-2">Revisar</span>}
                  {it.suggestion?.status === "pending" && (
                    <div
                      className="border border-primary-subtle rounded p-2 mt-2 small"
                      data-testid="ai-suggestion"
                    >
                      <div>
                        <strong>Sugestão da IA:</strong>{" "}
                        {it.suggestion.proposal.action === "match"
                          ? it.suggestion.proposal.productName
                          : `cadastrar "${it.suggestion.proposal.newProductName ?? it.description}"`}
                        {it.suggestion.proposal.factorQ4 !== 10_000 && (
                          <> · fator {formatQty(it.suggestion.proposal.factorQ4, 0)}</>
                        )}
                        {it.suggestion.confidencePct4 !== null && (
                          <span className="text-muted">
                            {" "}
                            · confiança {Math.round(it.suggestion.confidencePct4 / 10_000)}%
                          </span>
                        )}
                      </div>
                      {it.suggestion.rationale && <div className="text-muted">{it.suggestion.rationale}</div>}
                      {editable && (
                        <div className="d-flex gap-2 mt-1">
                          <button
                            type="button"
                            className="btn btn-sm btn-success"
                            disabled={decide.isPending}
                            onClick={() => decide.mutate({ id: it.suggestion!.id, decision: "accept" })}
                          >
                            Aceitar
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            disabled={decide.isPending}
                            onClick={() => decide.mutate({ id: it.suggestion!.id, decision: "reject" })}
                          >
                            Recusar
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </td>
                <td className="text-end text-nowrap">
                  {it.productId || it.createProduct ? (
                    <>
                      {formatQty(it.entryQtyQ4)} {it.productUnit ?? (it.factorQ4 === 10_000 ? it.unit : "UN")}
                      <div className="text-muted small">{formatBRL(it.unitCostCents)} / un.</div>
                    </>
                  ) : (
                    "—"
                  )}
                </td>
                {editable && (
                  <td className="text-end">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      aria-label={`Vincular item ${it.position}`}
                      onClick={() => setEditing(it)}
                    >
                      Vincular
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ItemDialog
        d={d}
        item={editing}
        onClose={() => setEditing(null)}
        onDone={(v) => {
          setEditing(null);
          onChange(v);
        }}
      />
    </div>
  );
}

type Choice = "product" | "new" | "none";

function ItemDialog({
  d,
  item,
  onClose,
  onDone,
}: {
  d: InboundDfeView;
  item: InboundDfeItemView | null;
  onClose(): void;
  onDone(v: InboundDfeView): void;
}): ReactElement {
  const { companyId, api } = useSession();
  const toast = useToast();
  const open = item !== null;
  const [choice, setChoice] = useState<Choice>("product");
  const [product, setProduct] = useState<{ id: string; name: string; unit: string } | null>(null);
  const [newName, setNewName] = useState("");
  const [factor, setFactor] = useState("1");
  const [q, setQ] = useState("");
  useEffect(() => {
    if (item) {
      setChoice(item.createProduct ? "new" : "product");
      setProduct(
        item.productId
          ? { id: item.productId, name: item.productName ?? "", unit: item.productUnit ?? "" }
          : null,
      );
      setNewName(item.newProductName ?? item.description.slice(0, 120));
      setFactor(formatQty(item.factorQ4, 0));
      setQ("");
    }
  }, [item]);
  const term = q.trim();
  const found = useQuery({
    queryKey: ["products", companyId, { q: term, picker: "inbound" }],
    enabled: open && choice === "product" && term.length >= 2,
    queryFn: () =>
      api<ListResult<ProductListItem>>(
        `/products?${new URLSearchParams({ q: term, format: "S", includeVariations: "yes", limit: "8" })}`,
      ),
  });
  const factorQ4 = parseFactor(factor);
  const factorError = factorQ4 === null ? "Informe um fator maior que zero (ex.: 12 ou 0,5)" : undefined;
  const send = useMutation({
    mutationFn: () =>
      api<InboundDfeView>(`/inbound-dfe/${d.id}/items/${item!.id}`, {
        method: "PUT",
        body: JSON.stringify({
          productId: choice === "product" ? (product?.id ?? null) : null,
          factorQ4,
          createProduct: choice === "new",
          newProductName: choice === "new" ? newName : null,
        }),
      }),
    onSuccess: (v) => {
      toast("success", "Vínculo salvo");
      onDone(v);
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const invalid =
    !!factorError || (choice === "product" && !product) || (choice === "new" && !newName.trim());
  return (
    <Modal
      open={open}
      title={item ? `Vincular item ${item.position}` : ""}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={send.isPending || invalid}
            onClick={() => send.mutate()}
          >
            Salvar vínculo
          </button>
        </>
      }
    >
      {item && (
        <>
          <p className="small">
            <strong>{item.description}</strong>
            <br />
            <span className="text-muted">
              Código {item.code} · {formatQty(item.quantityQ4)} {item.unit}
              {item.taxUnit && item.taxUnit !== item.unit && item.taxQuantityQ4 !== null && (
                <>
                  {" "}
                  ({formatQty(item.taxQuantityQ4)} {item.taxUnit})
                </>
              )}
            </span>
          </p>
          <fieldset className="mb-3">
            <legend className="form-label fs-6">Vínculo</legend>
            {(
              [
                ["product", "Produto cadastrado"],
                ["new", "Cadastrar produto novo"],
                ["none", "Sem vínculo"],
              ] as const
            ).map(([v, label]) => (
              <div className="form-check form-check-inline" key={v}>
                <input
                  id={`item-choice-${v}`}
                  type="radio"
                  name="item-choice"
                  className="form-check-input"
                  checked={choice === v}
                  onChange={() => setChoice(v)}
                />
                <label className="form-check-label" htmlFor={`item-choice-${v}`}>
                  {label}
                </label>
              </div>
            ))}
          </fieldset>
          {choice === "product" && (
            <div className="mb-3">
              {product && (
                <div className="alert alert-secondary py-2 small" data-testid="item-product">
                  Produto: <strong>{product.name}</strong>
                  {product.unit && <span className="text-muted"> ({product.unit})</span>}
                </div>
              )}
              <label className="form-label" htmlFor="inbound-product-search">
                Buscar produto
              </label>
              <input
                id="inbound-product-search"
                type="search"
                className="form-control"
                placeholder="Digite nome, SKU ou GTIN do produto"
                value={q}
                data-autofocus
                onChange={(e) => setQ(e.target.value)}
              />
              {term.length >= 2 && (
                <ul className="list-group mt-1" aria-label="Produtos encontrados">
                  {found.isLoading && <li className="list-group-item text-muted">Buscando…</li>}
                  {found.isSuccess && found.data.data.length === 0 && (
                    <li className="list-group-item text-muted">Nenhum produto encontrado.</li>
                  )}
                  {found.data?.data.map((p) => (
                    <li key={p.id} className="list-group-item d-flex align-items-center gap-2">
                      <span className="flex-grow-1">
                        {p.name}
                        {p.sku && <span className="text-muted small ms-2">{p.sku}</span>}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        aria-label={`Escolher ${p.name}`}
                        onClick={() => {
                          setProduct({ id: p.id, name: p.name, unit: p.unit });
                          setQ("");
                        }}
                      >
                        Escolher
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {choice === "new" && (
            <div className="row">
              <Field label="Nome do produto novo" required className="col-12">
                <input
                  className="form-control"
                  maxLength={120}
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
              </Field>
            </div>
          )}
          {choice !== "none" && (
            <div className="row">
              <Field
                label="Fator de conversão"
                required
                className="col-md-6"
                error={factorError}
                hint={`Quantas unidades do produto vêm em 1 ${item.unit} da nota`}
              >
                <input
                  className="form-control text-end"
                  inputMode="decimal"
                  value={factor}
                  onChange={(e) => setFactor(e.target.value)}
                />
              </Field>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

/** "12" / "0,5" → q4; null se inválido ou ≤ 0. */
function parseFactor(s: string): number | null {
  const m = /^\s*(\d{1,6})(?:[,.](\d{1,4}))?\s*$/.exec(s);
  if (!m) return null;
  const v = Number(m[1]) * 10_000 + Number((m[2] ?? "").padEnd(4, "0"));
  return v > 0 ? v : null;
}

// ---------- lançamento ----------

/** "Nº 12 — 01/10/2026 — 2 de 2 produtos" (+ aviso quando a nota passa do saldo). */
function poOptionLabel(c: InboundPurchaseOrderCandidate): string {
  const base = `Nº ${c.number}${c.purchaseRef ? ` (${c.purchaseRef})` : ""} — ${formatDateBR(c.date)} — ${c.matchedProducts} de ${c.invoiceProducts} produtos`;
  return c.fits ? base : `${base} · a nota passa do saldo`;
}

function EntryCard({
  d,
  perms,
  onChange,
}: {
  d: InboundDfeView;
  perms: Permissions;
  onChange(d: InboundDfeView): void;
}): ReactElement {
  const { companyId, api } = useSession();
  const toast = useToast();
  const canImport = can(perms, "inbound_invoices", "create");
  const canReverse = can(perms, "inbound_invoices", "update");
  const canStock = can(perms, "stock", "create");
  const canPayables = can(perms, "payables", "create");
  const warehouses = useQuery({
    queryKey: ["warehouses", companyId],
    enabled: canImport && d.importStatus !== "imported",
    queryFn: () => api<{ data: WarehouseView[] }>("/warehouses"),
  });
  const active = (warehouses.data?.data ?? []).filter((w) => w.active);
  const [warehouseId, setWarehouseId] = useState("");
  // Pedido de compra (T2.6): a nota vira o recebimento do pedido escolhido (mesmo fornecedor, com saldo).
  const canLinkPo = can(perms, "purchase_orders", "read") && can(perms, "purchase_orders", "update");
  const linked = d.items.map((i) => `${i.productId ?? ""}:${i.factorQ4}`).join(",");
  const candidates = useQuery({
    queryKey: ["purchase-orders", companyId, "inbound", d.id, linked],
    enabled: canImport && canLinkPo && d.importStatus !== "imported",
    queryFn: () => api<{ data: InboundPurchaseOrderCandidate[] }>(`/inbound-dfe/${d.id}/purchase-orders`),
  });
  const [purchaseOrderId, setPurchaseOrderId] = useState("");
  const [postStock, setPostStock] = useState(canStock);
  const [postPayables, setPostPayables] = useState(canPayables);
  const [confirmReverse, setConfirmReverse] = useState(false);
  const [importKey, setImportKey] = useState(() => crypto.randomUUID());
  const [reverseKey, setReverseKey] = useState(() => crypto.randomUUID());
  const wh = warehouseId || active.find((w) => w.isDefault)?.id || active[0]?.id || "";
  const post = useMutation({
    mutationFn: () =>
      api<InboundDfeView>(`/inbound-dfe/${d.id}/import`, {
        method: "POST",
        headers: { "Idempotency-Key": importKey },
        body: JSON.stringify({
          warehouseId: postStock ? wh || null : null,
          postStock,
          postPayables,
          purchaseOrderId: purchaseOrderId || null,
        }),
      }),
    onSuccess: (v) => {
      toast("success", "Entrada lançada");
      setReverseKey(crypto.randomUUID());
      onChange(v);
    },
    onError: (e) => {
      toast("danger", errorMessage(e));
      setImportKey(crypto.randomUUID());
    },
  });
  const reverse = useMutation({
    mutationFn: () =>
      api<InboundDfeView>(`/inbound-dfe/${d.id}/reverse`, {
        method: "POST",
        headers: { "Idempotency-Key": reverseKey },
      }),
    onSuccess: (v) => {
      toast("success", "Entrada estornada");
      setImportKey(crypto.randomUUID());
      onChange(v);
    },
    onError: (e) => {
      toast("danger", errorMessage(e));
      setReverseKey(crypto.randomUUID());
    },
  });
  if (d.importStatus === "imported" && d.entry) {
    return (
      <div className="box card mb-3">
        <div className="card-body">
          <h2 className="h6">Entrada lançada</h2>
          <p className="small mb-2" data-testid="inbound-entry">
            {d.entry.importedAt && <>Lançada em {dateTimeBR(d.entry.importedAt)}. </>}
            {d.entry.movements} {d.entry.movements === 1 ? "movimento" : "movimentos"} de estoque ·{" "}
            {d.entry.titles} {d.entry.titles === 1 ? "conta a pagar" : "contas a pagar"}
          </p>
          {d.purchaseOrder && (
            <p className="small mb-2" data-testid="inbound-purchase-order">
              Recebimento {d.purchaseOrder.receiptNumber} do{" "}
              {can(perms, "purchase_orders", "read") ? (
                <Link to="/suprimentos/pedidos-de-compra/$id" params={{ id: d.purchaseOrder.id }}>
                  pedido de compra nº {d.purchaseOrder.number}
                </Link>
              ) : (
                <>pedido de compra nº {d.purchaseOrder.number}</>
              )}
              .
            </p>
          )}
          {canReverse && (
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={() => setConfirmReverse(true)}
            >
              Estornar entrada
            </button>
          )}
        </div>
        <ConfirmDialog
          open={confirmReverse}
          title="Estornar entrada"
          message="O estoque recebido sai de novo (com o mesmo custo) e as contas a pagar geradas por ela são canceladas. A nota volta para não lançada."
          confirmLabel="Estornar"
          onConfirm={() => {
            setConfirmReverse(false);
            reverse.mutate();
          }}
          onCancel={() => setConfirmReverse(false)}
        />
      </div>
    );
  }
  if (!canImport) return <></>;
  return (
    <div className="box card mb-3">
      <div className="card-body">
        <h2 className="h6">Lançar entrada</h2>
        {d.importStatus === "reversed" && d.entry?.reversedAt && (
          <p className="small text-muted">Entrada anterior estornada em {dateTimeBR(d.entry.reversedAt)}.</p>
        )}
        {!d.sefazConfirmed && d.importStatus !== "imported" && (
          <div className="alert alert-warning small" role="note" data-testid="inbound-unverified">
            XML enviado manualmente e ainda não confirmado pela distribuição da SEFAZ: a assinatura digital do
            arquivo não é conferida. Confira emitente, valores e duplicatas com o fornecedor antes de gerar as
            contas a pagar.
          </div>
        )}
        {d.importBlockers.length > 0 && (
          <ul className="small text-danger mb-3" data-testid="inbound-blockers">
            {d.importBlockers.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        )}
        <div className="row g-3 align-items-end">
          <div className="col-md-4">
            <div className="form-check">
              <input
                id="entry-stock"
                type="checkbox"
                className="form-check-input"
                checked={postStock}
                disabled={!canStock}
                onChange={(e) => setPostStock(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="entry-stock">
                Lançar no estoque (com custo)
              </label>
            </div>
            <div className="form-check">
              <input
                id="entry-payables"
                type="checkbox"
                className="form-check-input"
                checked={postPayables}
                disabled={!canPayables}
                onChange={(e) => setPostPayables(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="entry-payables">
                Gerar contas a pagar ({d.installments.length || 1}{" "}
                {(d.installments.length || 1) === 1 ? "parcela" : "parcelas"})
              </label>
            </div>
          </div>
          {canLinkPo && (candidates.data?.data.length ?? 0) > 0 && (
            <div className="col-12">
              <label className="form-label" htmlFor="entry-purchase-order">
                Pedido de compra
              </label>
              <select
                id="entry-purchase-order"
                className="form-select"
                value={purchaseOrderId}
                onChange={(e) => setPurchaseOrderId(e.target.value)}
              >
                <option value="">Nenhum (entrada avulsa)</option>
                {candidates.data!.data.map((c) => (
                  <option key={c.id} value={c.id}>
                    {poOptionLabel(c)}
                  </option>
                ))}
              </select>
              <div className="form-text">
                A nota passa a ser o recebimento do pedido: o saldo dele baixa pelas quantidades da nota, sem
                lançar estoque ou contas em dobro.
              </div>
            </div>
          )}
          {postStock && (
            <div className="col-md-4">
              <label className="form-label" htmlFor="entry-warehouse">
                Depósito
              </label>
              <select
                id="entry-warehouse"
                className="form-select"
                value={wh}
                onChange={(e) => setWarehouseId(e.target.value)}
              >
                {active.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="col-md-4">
            <button
              type="button"
              className="btn btn-primary"
              disabled={post.isPending || d.importBlockers.length > 0 || (!postStock && !postPayables)}
              onClick={() => post.mutate()}
            >
              {post.isPending ? "Lançando…" : "Lançar entrada"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
